import { z } from 'zod';
import { ADJUSTMENT_KINDS, DISPUTE_REASONS, INVOICE_SOURCES, INVOICE_STATUSES, PAYMENT_METHODS, VENDOR_TYPES, } from './enums.js';
import { dateSchema, idSchema, moneySchema, nonNegativeMoneySchema, optionalDateSchema, paginationQuerySchema, booleanQuery, } from './common.schema.js';
/* ────────────────────────────── Read models ────────────────────────────── */
/**
 * One slice of an invoice charged to one project.
 *
 * Today the UI always creates exactly one allocation per invoice, matching how
 * the client's spreadsheet works. The join table exists so splitting an invoice
 * across several projects later is a UI change, not a data migration.
 */
export const invoiceAllocationSchema = z.object({
    id: idSchema,
    /** `null` means a company-wide cost with no project ("BIURO" in the spreadsheet). */
    projectId: idSchema.nullable(),
    projectCode: z.string().nullable(),
    projectName: z.string().nullable(),
    managerId: idSchema.nullable(),
    managerName: z.string().nullable(),
    netAmount: moneySchema,
});
/**
 * One position on the invoice.
 *
 * These are what let the app render the document itself instead of depending
 * on a scan — which matters because KSeF hands over data with no PDF.
 */
export const invoiceLineItemSchema = z.object({
    id: idSchema,
    position: z.number().int().min(1),
    name: z.string(),
    unit: z.string().nullable(),
    quantity: z.number(),
    unitPrice: moneySchema,
    netAmount: moneySchema,
    vatRate: z.number().int(),
    vatAmount: moneySchema,
    grossAmount: moneySchema,
});
/** What the accountant types when keying an invoice by hand. */
export const lineItemInputSchema = z.object({
    name: z.string().min(1, 'Podaj nazwę pozycji').max(200),
    unit: z.string().max(12).nullish(),
    quantity: z.number().positive('Ilość musi być większa od zera'),
    unitPrice: nonNegativeMoneySchema,
    vatRate: z.number().int().min(0).max(100).default(23),
});
export const invoiceAttachmentSchema = z.object({
    id: idSchema,
    originalName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    uploadedAt: dateSchema,
});
export const invoiceStatusChangeSchema = z.object({
    id: idSchema,
    fromStatus: z.enum(INVOICE_STATUSES).nullable(),
    toStatus: z.enum(INVOICE_STATUSES),
    reason: z.enum(DISPUTE_REASONS).nullable(),
    comment: z.string().nullable(),
    userName: z.string(),
    createdAt: dateSchema,
});
/**
 * One transfer against the invoice. Several rows = "zapłata częściowa"
 * (24.09.2026): the invoice is PAID only once the rows cover the amount due.
 */
export const invoicePaymentSchema = z.object({
    id: idSchema,
    amount: moneySchema,
    paidAt: dateSchema,
    note: z.string().nullable(),
    /** Null when the row came from a migration or a seed. */
    createdByName: z.string().nullable(),
    createdAt: dateSchema,
});
/** One line under the invoice explaining why the amount due differs from gross. */
export const invoiceAdjustmentSchema = z.object({
    id: idSchema,
    kind: z.enum(ADJUSTMENT_KINDS),
    /** Positive lowers the amount due, negative raises it. */
    amount: moneySchema,
    documentNumber: z.string().nullable(),
    comment: z.string().nullable(),
    /** Null when the row came from a migration or a seed. */
    createdByName: z.string().nullable(),
    createdAt: dateSchema,
});
export const invoiceSchema = z.object({
    id: idSchema,
    number: z.string(),
    status: z.enum(INVOICE_STATUSES),
    source: z.enum(INVOICE_SOURCES),
    /** Cash invoices are settled at the counter and never reach payables. */
    paymentMethod: z.enum(PAYMENT_METHODS),
    /**
     * Where the invoice landed in the ledger, written after it was booked.
     * Its only job is to lead from this screen back to the source document.
     */
    ledgerRef: z.string().nullable(),
    vendorId: idSchema,
    vendorName: z.string(),
    vendorType: z.enum(VENDOR_TYPES),
    vendorNip: z.string().nullable(),
    vendorAddress: z.string().nullable(),
    categoryId: idSchema.nullable(),
    categoryName: z.string().nullable(),
    issueDate: dateSchema,
    receivedDate: dateSchema,
    dueDate: dateSchema.nullable(),
    /**
     * When the invoice *and* every document it depends on were in hand.
     * Subcontractor terms run from this date, not from the invoice date:
     * "… dni od dostarczenia poprawnie wystawionej faktury wraz z podpisanym protokołem".
     */
    documentsCompleteAt: dateSchema.nullable(),
    paidAt: dateSchema.nullable(),
    netAmount: moneySchema,
    vatAmount: moneySchema,
    grossAmount: moneySchema,
    /**
     * Warranty retention withheld from a subcontractor's invoice — the sum of
     * the `RETENTION` adjustments, kept as a field because payables show it as
     * its own column.
     */
    retentionAmount: moneySchema,
    /** Sum of every adjustment, retention included. Signed. */
    adjustmentsTotal: moneySchema,
    /** `grossAmount - adjustmentsTotal`, computed by the API. Zero once paid. */
    amountDue: moneySchema,
    adjustments: z.array(invoiceAdjustmentSchema),
    /** Sum of recorded transfers. Equals `amountDue` once the invoice is paid. */
    paidAmount: moneySchema,
    /** What is still owed: `amountDue - paidAmount`. Zero once paid. */
    remainingAmount: moneySchema,
    /** Transfer history, oldest first. Empty on cash and legacy invoices. */
    payments: z.array(invoicePaymentSchema),
    requiresHandoverProtocol: z.boolean(),
    notes: z.string().nullable(),
    /** Released for payment by an accountant, not the owner. Permanent. */
    approvedForPaymentWithoutOwner: z.boolean(),
    /**
     * The owner's after-the-fact sign-off on such a release (24.09.2026).
     * Set once by the owner; from then on the invoice stops being flagged and
     * leaves the owner's counter, while the fact above stays on record.
     */
    ownerApprovedAt: dateSchema.nullable(),
    ownerApprovedByName: z.string().nullable(),
    /**
     * Correction linkage (24.09.2026). Set = this document is a correcting
     * invoice: its amounts are the signed delta, it has no approval flow and is
     * never itself a liability. The referenced invoice is the one it corrects.
     */
    correctsInvoiceId: idSchema.nullable(),
    correctsNumber: z.string().nullable(),
    /** When the delta was accounted for; null = hangs in payables as a balance. */
    correctionSettledAt: dateSchema.nullable(),
    correctionSettledByName: z.string().nullable(),
    /** Corrections pointing at THIS invoice — the other end of the link. */
    correctedBy: z.array(z.object({ id: idSchema, number: z.string() })),
    /**
     * Soft delete. A deleted invoice is out of every list and sum but keeps its
     * scans and history, and can be restored from the "Usunięte" view.
     */
    deletedAt: dateSchema.nullable(),
    deletedByName: z.string().nullable(),
    allocations: z.array(invoiceAllocationSchema),
    attachments: z.array(invoiceAttachmentSchema),
    /**
     * Who the invoice was bought for — the odzież/paliwo case. Empty on almost
     * every invoice: most costs belong to a site, not to a person.
     */
    employees: z.array(z.object({ id: idSchema, fullName: z.string() })),
    /** Empty when nobody keyed the positions — the totals still stand on their own. */
    lineItems: z.array(invoiceLineItemSchema),
    /**
     * Negative once the due date has passed. `null` when the invoice carries no
     * term at all, or is already paid — never merely because a document is
     * missing, which used to hide terms that had long since run out.
     */
    daysUntilDue: z.number().int().nullable(),
    isOverdue: z.boolean(),
    /** Days the invoice has been sitting with a site manager. */
    daysAwaitingManager: z.number().int().nullable(),
    createdAt: dateSchema,
    updatedAt: dateSchema,
});
export const invoiceDetailSchema = invoiceSchema.extend({
    statusHistory: z.array(invoiceStatusChangeSchema),
});
/* ─────────────────────────────── Write models ──────────────────────────── */
const amountsSchema = z.object({
    netAmount: moneySchema,
    vatAmount: moneySchema,
    grossAmount: moneySchema,
});
/** Gross must equal net + VAT; catching this at entry beats reconciling later. */
const withConsistentAmounts = (schema) => schema.refine((value) => Math.round(value.grossAmount * 100) ===
    Math.round(value.netAmount * 100) + Math.round(value.vatAmount * 100), { message: 'Kwota brutto musi być sumą netto i VAT', path: ['grossAmount'] });
/** Positions, when present, must add up to the invoice total. */
const withMatchingLineItems = (schema) => schema.refine((value) => {
    const items = value.lineItems ?? [];
    if (items.length === 0)
        return true;
    const itemsNet = items.reduce((total, item) => total + Math.round(item.quantity * item.unitPrice * 100), 0);
    return itemsNet === Math.round(value.netAmount * 100);
}, { message: 'Suma pozycji musi być równa kwocie netto faktury', path: ['lineItems'] });
/**
 * A payment date may only accompany a cash invoice: the money left at the
 * counter, before the document ever entered the flow. A transfer is recorded
 * as paid at the end of the flow, by `markInvoicePaidSchema`.
 */
const withCashPaidAt = (schema) => schema.refine((value) => !value.paidAt || value.paymentMethod === 'CASH', {
    message: 'Datę zapłaty przy dodawaniu można podać tylko dla faktury gotówkowej',
    path: ['paidAt'],
});
export const createInvoiceSchema = withCashPaidAt(withMatchingLineItems(withConsistentAmounts(amountsSchema.extend({
    number: z.string().min(1, 'Podaj numer faktury').max(64),
    vendorId: idSchema,
    issueDate: dateSchema,
    receivedDate: dateSchema.optional(),
    dueDate: optionalDateSchema,
    paymentMethod: z.enum(PAYMENT_METHODS).default('TRANSFER'),
    /**
     * Cash only (decision of 3.09.2026, variant "ręcznie"): the day the
     * receipt was paid. The invoice still goes through approval, but the
     * counters stop listing it as outstanding from day one.
     */
    paidAt: optionalDateSchema,
    notes: z.string().max(1000).nullish(),
    /** Optional: a fuel receipt does not need its positions keyed in. */
    lineItems: z.array(lineItemInputSchema).max(50).default([]),
    /**
     * Set = this is a correcting invoice for the given document, and the
     * amounts above are the signed DELTA (negative lowers the original).
     * The API decides what happens next: original unpaid → its amount due
     * changes at once; original paid → the correction waits in payables.
     */
    correctsInvoiceId: idSchema.nullish(),
}))));
/**
 * A correction whose delta is not accounted for yet — the vendor's ± balance
 * the payables screen must keep in sight ("żeby nam to nie umknęło").
 */
export const unsettledCorrectionSchema = z.object({
    id: idSchema,
    number: z.string(),
    vendorId: idSchema,
    vendorName: z.string(),
    issueDate: dateSchema,
    /** Signed: negative = the vendor owes us, positive = we owe more. */
    deltaAmount: moneySchema,
    correctsInvoiceId: idSchema,
    correctsNumber: z.string(),
});
/**
 * Settling a correction: against a concrete unpaid invoice of the same vendor
 * (its amount due moves by the delta), or — with no target — by hand, when the
 * money went back outside the system.
 */
export const settleCorrectionSchema = z.object({
    targetInvoiceId: idSchema.nullish(),
});
export const updateInvoiceSchema = withConsistentAmounts(amountsSchema.extend({
    number: z.string().min(1).max(64),
    vendorId: idSchema,
    issueDate: dateSchema,
    dueDate: optionalDateSchema,
    paymentMethod: z.enum(PAYMENT_METHODS).default('TRANSFER'),
    notes: z.string().max(1000).nullish(),
}));
/**
 * What accounting records *about* the invoice rather than about the workflow:
 * where it was booked, and how it is settled.
 *
 * Separate from `updateInvoiceSchema` because it stays editable at every
 * status, including after payment — the ledger position is written once the
 * document is booked, which is always after the invoice stopped moving.
 */
export const updateBookkeepingSchema = withCashPaidAt(z.object({
    /** Empty string is normalised to `null`: "no position" has one representation. */
    ledgerRef: z
        .string()
        .trim()
        .max(64, 'Pozycja księgowa może mieć najwyżej 64 znaki')
        .nullish()
        .transform((value) => value || null),
    paymentMethod: z.enum(PAYMENT_METHODS),
    /**
     * Set when an invoice is switched to cash after the fact and has no payment
     * date yet. Never clears an existing date: `undefined`/`null` means "leave it".
     */
    paidAt: optionalDateSchema,
}));
/** What the accountant fills in on the "assign" screen. */
export const assignInvoiceSchema = z.object({
    /** `null` books the invoice as a company-wide cost. */
    projectId: idSchema.nullable(),
    managerId: idSchema.nullable(),
    categoryId: idSchema,
    dueDate: optionalDateSchema,
    retentionAmount: nonNegativeMoneySchema.default(0),
    requiresHandoverProtocol: z.boolean().default(false),
    notifyManager: z.boolean().default(true),
});
export const disputeInvoiceSchema = z.object({
    reason: z.enum(DISPUTE_REASONS),
    comment: z.string().max(1000).nullish(),
});
export const approveInvoicesSchema = z.object({
    invoiceIds: z.array(idSchema).min(1, 'Zaznacz co najmniej jedną fakturę'),
});
export const markInvoicePaidSchema = z.object({
    paidAt: dateSchema,
});
/**
 * One recorded transfer. The date defaults to "today" in the UI, exactly as
 * agreed on 24.09.2026 — the amount is the only thing the accountant types.
 */
export const registerPaymentSchema = z.object({
    amount: moneySchema.min(0.01, 'Kwota wpłaty musi być większa od zera'),
    paidAt: dateSchema,
    note: z.string().max(200).nullish(),
});
/** Recorded when the missing handover protocol finally arrives. */
export const completeDocumentsSchema = z.object({
    documentsCompleteAt: dateSchema,
    dueDate: optionalDateSchema,
});
/* ─────────────────────────────── Queries ───────────────────────────────── */
/**
 * A set of ids that survives a query string.
 *
 * Accepts `?employeeIds=a,b` and a real array alike, so the same schema parses
 * a URL on the server and a typed object on the client. Empty means "no filter"
 * rather than "match nothing" — an emptied checkbox list must widen the list
 * back out, not blank it.
 */
const idListQuery = z
    .union([z.string(), z.array(idSchema)])
    .optional()
    .transform((value) => {
    const ids = typeof value === 'string' ? value.split(',') : (value ?? []);
    const cleaned = ids.map((id) => id.trim()).filter(Boolean);
    return cleaned.length > 0 ? cleaned : undefined;
});
export const invoiceSortFields = [
    'number',
    'issueDate',
    'receivedDate',
    'dueDate',
    'netAmount',
    'grossAmount',
    'vendorName',
    'status',
    'paidAt',
];
export const invoiceListQuerySchema = paginationQuerySchema.extend({
    search: z.string().trim().optional(),
    status: z.enum(INVOICE_STATUSES).optional(),
    projectId: idSchema.optional(),
    vendorId: idSchema.optional(),
    categoryId: idSchema.optional(),
    /**
     * „Ile wydaliśmy na Kowalskiego” — the whole point of naming people at all.
     *
     * Several at once means *any of them*, not all: the question is what a group
     * of people cost together, never which single invoice covers all of them.
     * Comma-separated on the wire so the filter survives a copied URL as one key.
     */
    employeeIds: idListQuery,
    issuedFrom: z.coerce.date().optional(),
    issuedTo: z.coerce.date().optional(),
    /**
     * The payment term, which is the window payables are read through: "co mam
     * do zapłaty w tym tygodniu" is a question about `dueDate`, not `issueDate`.
     */
    dueFrom: z.coerce.date().optional(),
    dueTo: z.coerce.date().optional(),
    onlyOverdue: booleanQuery(false),
    onlyUnpaid: booleanQuery(false),
    /**
     * Drops cash invoices. Payables set it permanently: an invoice paid at the
     * counter is not an outstanding liability, so listing it there would inflate
     * the one number that screen exists to answer.
     */
    excludeCash: booleanQuery(false),
    /** The mirror image: only invoices paid at the counter. */
    onlyCash: booleanQuery(false),
    /** Subcontractor invoices still waiting for their handover protocol. */
    missingProtocol: booleanQuery(false),
    /**
     * Invoices an accountant released for payment in the owner's place — and
     * the owner has not signed off on yet. Once `ownerApprovedAt` is set the
     * invoice leaves this filter; the permanent fact stays on its detail view.
     */
    withoutOwnerApproval: booleanQuery(false),
    /**
     * The "Usunięte" view: *only* soft-deleted invoices, instead of the live
     * register. Not a filter that mixes into the others — a deleted invoice and
     * a live one never belong on the same list.
     */
    deleted: booleanQuery(false),
    /**
     * Lets the register show invoices of archived sites again. Off by default:
     * an archived budowa leaves the daily lists whole (24.09.2026), and its own
     * card — reached from "Archiwum budów" — is where its rows still live.
     */
    includeArchived: booleanQuery(false),
    /**
     * Restricts the list to invoices allocated to the calling site manager.
     *
     * Reading is scoped per project, but approving requires being the named
     * manager — without this filter the queue offers buttons the API rejects.
     */
    assignedToMe: booleanQuery(false),
    sortBy: z.enum(invoiceSortFields).default('receivedDate'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
/** Powers the "8 of the last 10 invoices from SIG went to Cholerzyn" hint. */
export const projectSuggestionSchema = z.object({
    projectId: idSchema,
    projectCode: z.string(),
    projectName: z.string(),
    managerId: idSchema.nullable(),
    managerName: z.string().nullable(),
    matchCount: z.number().int(),
    sampleSize: z.number().int(),
});
//# sourceMappingURL=invoice.schema.js.map